import * as yup from 'yup';
import { IFormSelectOption } from '../../../../interfaces/forms/forms';
import { isItemWeightRequired } from './stepExportForm/helpers';

interface IContentsLine {
	amountInPackage: '' | number;
	unitPrice: string;
	// Derived value (unitPrice × amountInPackage). Not user-editable; kept in the model because
	// the API mapper and confirmation overview read it.
	price: string;
	// Weight per item (kg), only used for USA/PR gift shipments; sent to the API as the line total.
	weight: string;
	contents: string;
	countryOfOrigin?: IFormSelectOption;
	hsTariffCode: string;
	// The chapter/heading titles that go with the selected HS code, kept for display only.
	hsChapterTitle?: string;
	hsHeadingTitle?: string;
	// True when the user typed the HS code by hand (lookup endpoint unavailable), so re-editing
	// the line reopens the manual input rather than the search.
	hsManualEntry?: boolean;
}

export interface IStepExportFormInfo {
	currency: IFormSelectOption;
	packageType: IFormSelectOption;
	contents: IContentsLine[];
	shipmentHasNoDangerousContents: boolean;
	ifProductIsNotShippable: '' | 'A' | 'X'; // A: endursenda, X: farga
	returnPolicy: IFormSelectOption;
	usaTerms?: boolean;
}

export const stepExportInitialValues: IStepExportFormInfo = {
	currency: null,
	packageType: null,
	contents: [
		{
			amountInPackage: '',
			unitPrice: '',
			price: '',
			weight: '',
			contents: '',
			countryOfOrigin: null,
			hsTariffCode: '',
			hsChapterTitle: '',
			hsHeadingTitle: '',
			hsManualEntry: false,
		},
	],
	shipmentHasNoDangerousContents: false,
	ifProductIsNotShippable: 'A',
	returnPolicy: null,
	usaTerms: false,
};

const yupFormSelectOption = {
	label: yup.string(),
	value: yup.string(),
};

export const buildStepExportSchema = (countryCode?: string, isInternationalLetter?: boolean) =>
	// Lazy so validation reacts to the live packageType: when USA/PR + Documents ('D') is chosen
	// the unit-price/price fields are hidden and forced to 0, so we must not require them.
	yup.lazy((values: any) => {
		const isUsaOrPr = countryCode === 'US' || countryCode === 'PR';
		const shouldHidePrice = isUsaOrPr && values?.packageType?.value === 'D';
		// Documents-only shipments (international letters, or USA/PR with package type 'D') ship a
		// single hardcoded documents line, so the per-item fields are not user-entered.
		const forcedDocuments = isInternationalLetter || shouldHidePrice;
		const showWeight = isItemWeightRequired(countryCode, values?.packageType?.value, isInternationalLetter);
		const unitPriceSchema = yup
			.string()
			.matches(/^[,0-9]+$/, 'step-export.amount-must-be-a-numer')
			.required('step-export.field-required');
		const weightSchema = yup
			.string()
			.matches(/^[,0-9]+$/, 'step-export.weight-must-be-a-number')
			.required('step-export.field-required')
			.test(
				'weight-not-zero',
				'step-export.weight-greater-then-zero',
				value => parseFloat(String(value ?? '').replace(',', '.')) > 0
			);

		return yup.object({
			currency: yup
			.object()
			.shape(yupFormSelectOption)
			.typeError('step-export.field-required')
			.required('step-export.field-required'),
		packageType: yup
			.object()
			.shape(yupFormSelectOption)
			.typeError('step-export.field-required')
			.required('step-export.field-required'),
		contents: yup.array().of(
			yup.object({
				amountInPackage: yup
					.number()
					.min(1, 'step-export.count-greater-then-zero')
					.typeError('step-export.count-must-be-a-number')
					.required('step-export.field-required'),
				// Unit price is hidden (and forced to 0) for USA/PR documents, so don't require it then.
				unitPrice: shouldHidePrice
					? yup.string()
					: forcedDocuments
					? unitPriceSchema
					: // A parcel must declare a real customs value. Only documents-only shipments
					  // (international letters, USA/PR documents) may declare 0, and those ship the
					  // hardcoded documents line instead of user-entered values.
					  unitPriceSchema.test(
							'customs-value-not-zero',
							'step-export.value-greater-then-zero',
							value => parseFloat(String(value ?? '').replace(',', '.')) > 0
					  ),
				weight: showWeight ? weightSchema : yup.string(),
				contents: yup.string().max(255, 'step-info.max').required('step-export.field-required'),
				// Required so the form is invalid without a selected code, but with no message —
				// there is nowhere sensible to show a per-item tariff error (a card only exists
				// once a code is chosen), so we rely on the invalid state alone.
				hsTariffCode: forcedDocuments ? yup.string() : yup.string().required(' '),
				countryOfOrigin:
					countryCode === 'US' || countryCode === 'PR'
						? yup
								.object()
								.shape({
									label: yup.string().required(),
									value: yup.string().required(),
								})
								.nullable()
								.typeError('step-export.field-required')
								.required('step-export.field-required')
						: yup
								.object()
								.shape({
									label: yup.string(),
									value: yup.string(),
								})
								.nullable()
								.notRequired(),
			})
		),
		shipmentHasNoDangerousContents: yup.boolean().oneOf([true], 'step-export.dangerous-not-allowed'),
		usaTerms:
			countryCode === 'US' || countryCode === 'PR'
				? yup.boolean().oneOf([true], 'step-export.field-required')
				: yup.boolean().notRequired(),
			ifProductIsNotShippable: yup.string().required('step-export.field-required'),
			returnPolicy: yup
				.object()
				.shape(yupFormSelectOption)
				.typeError('step-export.field-required')
				.required('step-export.field-required'),
		});
	});
